/**
 * Academy URL utilities for handling redirects through the resolver system
 */

const ACADEMY_DOMAIN = 'academy.honeybook.com';
const PARTNER_ACADEMY_DOMAIN = 'partneracademy.honeybook.com';
const ACADEMY_RESOLVER_PATH = '/app/academy/resolve';

/**
 * Builds the academy resolver URL with optional path parameter
 * @param path - The academy path to redirect to (e.g., '/student/path/123')
 * @returns The resolver URL that will handle SSO and redirect
 */
const buildAcademyResolverUrl = (path?: string): string => {
    const baseUrl = ACADEMY_RESOLVER_PATH;
    if (!path) {
        return baseUrl;
    }

    // Ensure path starts with '/' if it doesn't already
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}?redirect=${encodeURIComponent(normalizedPath)}`;
};

/**
 * Extracts the academy path from a full academy URL
 * @param fullUrl - Full academy URL (e.g., 'https://academy.honeybook.com/student/path/123')
 * @returns The extracted path or null if not a valid academy URL
 */
const extractAcademyPath = (fullUrl: string): string | null => {
    try {
        const url = new URL(fullUrl);

        // Check if it's an academy domain (including partner academy)
        if (
            url.hostname !== ACADEMY_DOMAIN &&
            url.hostname !== `www.${ACADEMY_DOMAIN}` &&
            url.hostname !== PARTNER_ACADEMY_DOMAIN &&
            url.hostname !== `www.${PARTNER_ACADEMY_DOMAIN}`
        ) {
            return null;
        }

        // Return the pathname (everything after the domain)
        return url.pathname + url.search + url.hash;
    } catch (error) {
        return null;
    }
};

/**
 * Get the resolver URL for a given academy path with intelligent handling
 * @param path - Academy path (e.g., '/student/path/123'), full URL, or undefined for home
 * @returns The resolver URL that will handle SSO and redirect
 */
export const getAcademyUrl = (path?: string): string => {
    if (!path) {
        return buildAcademyResolverUrl();
    }

    // If it's already a full academy URL, extract the path
    if (isAcademyUrl(path)) {
        const extractedPath = extractAcademyPath(path);
        return buildAcademyResolverUrl(extractedPath || undefined);
    }

    // Otherwise treat it as a path
    return buildAcademyResolverUrl(path);
};

/**
 * Checks if a URL is an academy URL (including partner academy)
 * @param url - URL to check
 * @returns True if it's an academy URL
 */
export const isAcademyUrl = (url: string): boolean => {
    try {
        const urlObj = new URL(url);
        return (
            urlObj.hostname === ACADEMY_DOMAIN ||
            urlObj.hostname === `www.${ACADEMY_DOMAIN}` ||
            urlObj.hostname === PARTNER_ACADEMY_DOMAIN ||
            urlObj.hostname === `www.${PARTNER_ACADEMY_DOMAIN}` ||
            url.includes(ACADEMY_DOMAIN) ||
            url.includes(PARTNER_ACADEMY_DOMAIN)
        );
    } catch (error) {
        return (
            url.includes(ACADEMY_DOMAIN) || url.includes(PARTNER_ACADEMY_DOMAIN)
        );
    }
};
